import { Query } from '@tanstack/react-query';

import { queryClient } from '@/services';

import {
  getActivePlanAssignmentsQueryKey,
  getManagedPlansQueryKey,
  getPlanAndAssignmentsQueryKey,
  getPlansSummaryQueryKey,
  listAssignableChecklistsQueryKey,
  listAssignedChecklistsQueryKey,
} from '@/_generated/api-clients/xp';
import { invalidateReportsPlans } from '@/routes/Reports/hooks/useGetPlansSummary';

interface GeneratedQueryKey {
  _id?: string;
  path?: Record<string, string>;
}

const getQueryKeyMeta = (query: Query): GeneratedQueryKey | undefined =>
  query.queryKey[0] as GeneratedQueryKey | undefined;

export const invalidatePlanReportQueries = async (planId: string) => {
  await Promise.all([
    invalidateReportsPlans(),
    queryClient.invalidateQueries({
      queryKey: [{ _id: 'getPlanReport', path: { planId } }],
      refetchType: 'all',
    }),
  ]);
};

export const invalidateUserSummaryReportQueries = async (
  userIds?: string[],
) => {
  if (userIds?.length) {
    await Promise.all(
      userIds.map(userId =>
        queryClient.invalidateQueries({
          queryKey: [{ _id: 'getUserSummaryReport', path: { userId } }],
          refetchType: 'all',
        }),
      ),
    );
    return;
  }

  await queryClient.invalidateQueries({
    queryKey: [{ _id: 'getUserSummaryReport' }],
    refetchType: 'all',
  });
};

export const invalidatePlanListQueries = async () => {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: getPlansSummaryQueryKey(),
      refetchType: 'all',
    }),
    queryClient.invalidateQueries({
      queryKey: getActivePlanAssignmentsQueryKey(),
      refetchType: 'all',
    }),
  ]);
};

export const invalidateUsersSummaryReportQueries = () =>
  queryClient.invalidateQueries({
    queryKey: [{ _id: 'getUsersSummaryReport' }],
    refetchType: 'all',
  });

/**
 * Invalidates all plan-related queries that reflect assignment/completion status.
 * Call after any mutation that changes plan status (complete, reset, reassign).
 */
export const invalidateAllPlanQueries = async (planId?: string) => {
  // Remove inactive query caches so they're forced to refetch on next mount.
  // Needed because the global queryClient has refetchOnMount: false.
  const inactiveKeys = [
    getActivePlanAssignmentsQueryKey(),
    listAssignedChecklistsQueryKey(),
    listAssignableChecklistsQueryKey(),
  ];
  for (const queryKey of inactiveKeys) {
    queryClient.removeQueries({ queryKey, type: 'inactive' });
  }

  const promises: Promise<void>[] = [
    queryClient.invalidateQueries({
      queryKey: getActivePlanAssignmentsQueryKey(),
      refetchType: 'all',
    }),
    queryClient.invalidateQueries({
      queryKey: listAssignedChecklistsQueryKey(),
      refetchType: 'all',
    }),
    queryClient.invalidateQueries({
      queryKey: getManagedPlansQueryKey(),
      refetchType: 'all',
    }),
    queryClient.invalidateQueries({
      queryKey: listAssignableChecklistsQueryKey(),
      refetchType: 'all',
    }),
  ];

  if (planId) {
    promises.push(
      queryClient.invalidateQueries({
        queryKey: getPlanAndAssignmentsQueryKey({ path: { planId } }),
        refetchType: 'all',
      }),
    );
  }

  await Promise.all(promises);
};

export const invalidateManagedPlansQueries = () =>
  queryClient.invalidateQueries({
    queryKey: getManagedPlansQueryKey(),
    refetchType: 'all',
  });

export const invalidatePlanAndAssignmentsQueries = (planId: string) =>
  queryClient.invalidateQueries({
    queryKey: getPlanAndAssignmentsQueryKey({ path: { planId } }),
    refetchType: 'all',
  });

/** @deprecated Invalidates legacy content-service report query IDs; remove once reportPlans/reportTeamMember/reportTeamMembers endpoints are fully retired. */
export const invalidateContentServiceReportQueries = () =>
  queryClient.invalidateQueries({
    predicate: query => {
      const key = getQueryKeyMeta(query);
      return (
        key?._id === 'reportPlans' ||
        key?._id === 'reportTeamMember' ||
        key?._id === 'reportTeamMembers'
      );
    },
    refetchType: 'all',
  });

export const invalidateCertificateQueries = async (
  planId: string,
  userIds?: string[],
) => {
  await Promise.all([
    invalidatePlanReportQueries(planId),
    invalidatePlanAndAssignmentsQueries(planId),
    invalidateManagedPlansQueries(),
    invalidateUserSummaryReportQueries(userIds),
    invalidatePlanListQueries(),
    invalidateUsersSummaryReportQueries(),
    invalidateContentServiceReportQueries(),
  ]);
};
